import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle, XCircle, Clock, Users, Camera, EnvelopeSimple, UploadSimple, Sparkle, Images, Newspaper, ShareNetwork, PenNib } from "@phosphor-icons/react";
import PhotosCmsTab from "@/pages/PhotosCmsTab";
import ObservationsV2Tab from "@/pages/ObservationsV2Tab";
import AuthoritiesTab from "@/pages/AuthoritiesTab";
import NewsTab from "@/pages/NewsTab";
import DistributionTab from "@/pages/DistributionTab";
import EditorialTab from "@/pages/EditorialTab";
import ExpertsTab from "@/pages/ExpertsTab";
import { Certificate } from "@phosphor-icons/react";

// Super-admin only console — Observations moderation + Users list.
// Access is gated by useAuth().user.is_admin. Non-admins redirect to /.

const TABS = [
  { key: "editorial", label: "Write Article", Icon: PenNib },
  { key: "experts", label: "Experts", Icon: Certificate },
  { key: "observations", label: "Observations", Icon: Camera },
  { key: "obs_v2", label: "Observation Network", Icon: Sparkle },
  { key: "news", label: "News & Stories", Icon: Newspaper },
  { key: "authorities", label: "Authorities", Icon: EnvelopeSimple },
  { key: "photos", label: "Photo CMS", Icon: Images },
  { key: "users", label: "Users", Icon: Users },
  { key: "bulk", label: "Bulk Import", Icon: UploadSimple },
  { key: "distribution", label: "Distribution", Icon: ShareNetwork },
];

function StatusBadge({ status }) {
  const map = {
    approved: { bg: "bg-emerald-500/15", text: "text-emerald-400", label: "Approved" },
    rejected: { bg: "bg-red-500/15", text: "text-red-400", label: "Rejected" },
    pending: { bg: "bg-amber-500/15", text: "text-amber-400", label: "Pending" },
  };
  const s = map[status] || map.pending;
  return (
    <span className={`font-mono-tech text-[10px] tracking-[0.25em] uppercase px-2 py-1 ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

function ObservationsTab() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const url = status ? `/admin/observations?status=${status}` : "/admin/observations";
      const { data } = await http.get(url);
      setItems(data.observations || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load observations");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status]);

  const moderate = async (obs_id, next) => {
    try {
      await http.post(`/admin/observations/${obs_id}/moderate`, { status: next });
      toast.success(`Marked ${next}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Moderation failed");
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {[
          ["", "All"],
          ["pending", "Pending"],
          ["approved", "Approved"],
          ["rejected", "Rejected"],
        ].map(([k, l]) => (
          <button
            key={k}
            data-testid={`admin-obs-filter-${k || 'all'}`}
            onClick={() => setStatus(k)}
            className={`font-mono-tech text-[10px] tracking-[0.3em] uppercase px-3 py-1.5 border transition-colors ${
              status === k
                ? "border-[color:var(--np-brand)] bg-[color:var(--np-brand)]/10 text-[color:var(--np-brand)]"
                : "border-black/20 text-[color:var(--np-text-2)] hover:border-[color:var(--np-text)]"
            }`}
          >{l}</button>
        ))}
        <button onClick={load} data-testid="admin-obs-refresh" className="ml-auto font-mono-tech text-[10px] tracking-[0.3em] uppercase px-3 py-1.5 border border-black/20 hover:border-[color:var(--np-text)]">
          Refresh
        </button>
      </div>

      {loading && <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="border border-black/15 p-10 text-center text-[color:var(--np-text-2)]">
          No observations to moderate.
        </div>
      )}

      <div className="space-y-3">
        {items.map((o) => (
          <div key={o.obs_id} data-testid={`admin-obs-row-${o.obs_id}`} className="border border-black/15 p-4 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
            <div className="md:col-span-2">
              {o.image_url ? (
                <img src={o.image_url} alt={o.category} className="w-full aspect-square object-cover" loading="lazy" />
              ) : (
                <div className="w-full aspect-square bg-black/5 flex items-center justify-center font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">
                  no photo
                </div>
              )}
            </div>
            <div className="md:col-span-6">
              <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-brand)]">
                {o.category} · {o.location}
              </div>
              <div className="font-serif-display text-xl leading-tight mt-1">{o.description}</div>
              <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-[color:var(--np-muted)] mt-2 flex flex-wrap items-center gap-2">
                <span>{o.user_name || "anonymous"} · {new Date(o.created_at).toLocaleString()}</span>
                {o.ai_assisted && (
                  <span data-testid={`admin-obs-ai-${o.obs_id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-[color:var(--np-brand)]/60 text-[color:var(--np-brand)] tracking-[0.2em]">
                    ✦ AI-assisted
                  </span>
                )}
                {o.is_first_in_region && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-emerald-500/60 text-emerald-500 tracking-[0.2em]">
                    endemic
                  </span>
                )}
              </div>
            </div>
            <div className="md:col-span-2">
              <StatusBadge status={o.moderation_status || "pending"} />
            </div>
            <div className="md:col-span-2 flex flex-wrap gap-2 justify-end">
              <button
                onClick={() => moderate(o.obs_id, "approved")}
                data-testid={`admin-obs-approve-${o.obs_id}`}
                className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.3em] uppercase px-3 py-1.5 border border-emerald-500/50 text-emerald-500 hover:bg-emerald-500/10"
              >
                <CheckCircle size={12} weight="bold" /> Approve
              </button>
              <button
                onClick={() => moderate(o.obs_id, "rejected")}
                data-testid={`admin-obs-reject-${o.obs_id}`}
                className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.3em] uppercase px-3 py-1.5 border border-red-500/50 text-red-500 hover:bg-red-500/10"
              >
                <XCircle size={12} weight="bold" /> Reject
              </button>
              <button
                onClick={() => moderate(o.obs_id, "pending")}
                className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.3em] uppercase px-3 py-1.5 border border-amber-500/50 text-amber-500 hover:bg-amber-500/10"
              >
                <Clock size={12} weight="bold" /> Reset
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/users");
      setUsers(data.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setTrust = async (u, next) => {
    const uid = u.firebase_uid || u.user_id;
    setBusy(uid);
    // Optimistic update
    setUsers((list) => list.map((x) => (x.firebase_uid || x.user_id) === uid ? { ...x, is_trusted: next } : x));
    try {
      await http.post(`/admin/users/${encodeURIComponent(uid)}/trust/set`, { is_trusted: next });
      toast.success(next ? `${u.name || "User"} is now a trusted contributor` : `Trust removed for ${u.name || "user"}`);
    } catch (e) {
      // Revert
      setUsers((list) => list.map((x) => (x.firebase_uid || x.user_id) === uid ? { ...x, is_trusted: !next } : x));
      toast.error(e?.response?.data?.detail || "Failed to update trust flag");
    } finally { setBusy(null); }
  };

  const filtered = users.filter((u) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return (u.name || "").toLowerCase().includes(needle) ||
           (u.email || "").toLowerCase().includes(needle) ||
           (u.firebase_uid || "").toLowerCase().includes(needle);
  });
  const trustedCount = users.filter((u) => u.is_trusted).length;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">
          {users.length} users · {users.filter(u => u.is_admin).length} admins · {trustedCount} trusted
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name / email / uid…"
          data-testid="admin-users-search"
          className="rounded-full border border-black/15 px-3 py-1.5 text-sm w-[280px] max-w-full focus:outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
        />
      </div>
      {loading && <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">Loading…</div>}
      <div className="border border-black/15 overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 border-b border-black/15 bg-black/5 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">
          <div className="col-span-3">User</div>
          <div className="col-span-3">Email / Phone</div>
          <div className="col-span-1">Provider</div>
          <div className="col-span-2">Joined</div>
          <div className="col-span-1 text-right">Admin</div>
          <div className="col-span-2 text-right">Trusted</div>
        </div>
        {filtered.map(u => {
          const uid = u.firebase_uid || u.user_id;
          const trusted = !!u.is_trusted;
          return (
            <div key={uid} data-testid={`admin-user-row-${uid}`} className="grid grid-cols-12 px-4 py-3 border-b border-black/10 last:border-0 items-center">
              <div className="col-span-3 flex items-center gap-3">
                <img
                  src={u.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.name || 'U')}&background=E4A15C&color=0A1620`}
                  alt={u.name}
                  className="w-8 h-8 rounded-full border border-black/15"
                />
                <div className="min-w-0">
                  <div className="font-serif-display text-base leading-tight truncate flex items-center gap-1.5">
                    {u.name || "—"}
                    {trusted && (
                      <span title="Trusted contributor — auto-approved posts" className="inline-block w-2 h-2 rounded-full bg-[color:var(--np-brand,#B85F2A)]" />
                    )}
                  </div>
                  <div className="font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[color:var(--np-muted)]">{uid.slice(0,16)}</div>
                </div>
              </div>
              <div className="col-span-3 text-sm text-[color:var(--np-text-2)] truncate">
                {u.email || u.phone || "—"}
              </div>
              <div className="col-span-1 font-mono-tech text-[10px] tracking-[0.25em] uppercase text-[color:var(--np-text-2)] truncate">
                {u.provider || "—"}
              </div>
              <div className="col-span-2 font-mono-tech text-[10px] tracking-[0.2em] uppercase text-[color:var(--np-muted)]">
                {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
              </div>
              <div className="col-span-1 text-right">
                {u.is_admin
                  ? <span className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-brand)]">Admin</span>
                  : <span className="text-[color:var(--np-muted)]">—</span>}
              </div>
              <div className="col-span-2 text-right">
                <button
                  onClick={() => setTrust(u, !trusted)}
                  disabled={busy === uid}
                  data-testid={`admin-user-trust-${uid}`}
                  aria-pressed={trusted}
                  title={trusted ? "Revoke trusted-contributor status" : "Make this member a trusted contributor — their posts auto-approve"}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase transition-colors disabled:opacity-50 ${
                    trusted
                      ? "border-[color:var(--np-brand,#B85F2A)] bg-[color:var(--np-brand,#B85F2A)] text-white hover:bg-[#a25526]"
                      : "border-black/20 bg-white text-[#1B4332] hover:bg-black/5"
                  }`}
                >
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${trusted ? "bg-white" : "bg-black/30"}`} />
                  {busy === uid ? "…" : (trusted ? "Trusted" : "Make trusted")}
                </button>
              </div>
            </div>
          );
        })}
        {!loading && filtered.length === 0 && (
          <div className="px-4 py-6 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-muted)]">
            No users match "{q}".
          </div>
        )}
      </div>
    </div>
  );
}

function ModeratorDigestButton() {
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/admin/moderator-digest/preview");
        setPending(data.pending_count);
      } catch { /* not admin yet */ }
    })();
  }, []);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/admin/moderator-digest/send");
      toast.success(`Digest sent to ${data.sent}/${data.recipients} moderators (${data.pending_count} pending)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Digest send failed");
    } finally { setBusy(false); }
  };

  return (
    <button
      onClick={send}
      disabled={busy}
      data-testid="admin-send-digest"
      className="inline-flex items-center gap-2 rounded-full border border-[color:var(--np-brand)] text-[color:var(--np-brand)] hover:bg-[color:var(--np-brand)] hover:text-white px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.3em] uppercase transition-colors disabled:opacity-50"
    >
      <EnvelopeSimple size={14} weight="fill" />
      {busy ? "Sending…" : pending != null ? `Send digest · ${pending} pending` : "Send digest"}
    </button>
  );
}


export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("editorial");

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">Loading…</div>
      </div>
    );
  }
  if (!user || !user.is_admin) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen pt-28 pb-24 px-6 md:px-10 max-w-[1400px] mx-auto">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-brand)] mb-3">
          NaturePulse · Super Admin
        </div>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-serif-display text-6xl md:text-8xl leading-[0.9] tracking-tighter">Console.</h1>
            <p className="mt-4 text-[color:var(--np-text-2)] max-w-2xl">
              Moderate community sightings and review the SocialPulse user directory.
            </p>
          </div>
          <ModeratorDigestButton />
        </div>
      </motion.div>

      <div className="mt-10 flex gap-1 border-b border-black/10">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-2 px-4 py-3 font-mono-tech text-[11px] tracking-[0.28em] uppercase border-b-2 transition-colors ${
              tab === t.key
                ? "border-[color:var(--np-brand)] text-[color:var(--np-text)]"
                : "border-transparent text-[color:var(--np-text-2)] hover:text-[color:var(--np-text)]"
            }`}
          >
            <t.Icon size={14} weight="thin" /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "editorial" && <EditorialTab />}
        {tab === "experts" && <ExpertsTab />}
        {tab === "observations" && <ObservationsTab />}
        {tab === "obs_v2" && <ObservationsV2Tab />}
        {tab === "news" && <NewsTab />}
        {tab === "authorities" && <AuthoritiesTab />}
        {tab === "photos" && <PhotosCmsTab />}
        {tab === "users" && <UsersTab />}
        {tab === "bulk" && <BulkImportTab />}
        {tab === "distribution" && <DistributionTab />}
      </div>
    </div>
  );
}

function BulkImportTab() {
  const [urls, setUrls] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const zipRef = useRef(null);

  const run = async () => {
    const lines = urls.split("\n").map((s) => s.trim()).filter(Boolean);
    if (lines.length === 0) return toast.error("Paste one photo URL per line.");
    if (lines.length > 25) return toast.error("Max 25 photos per batch.");
    setBusy(true); setResult(null);
    try {
      const items = lines.map((line) => {
        const parts = line.split("|");
        const out = { image_url: parts[0].trim() };
        if (parts[1] && parts[1].includes(",")) {
          const [la, lo] = parts[1].split(",").map((s) => parseFloat(s.trim()));
          if (!isNaN(la) && !isNaN(lo)) { out.lat = la; out.lon = lo; }
        }
        if (parts[2]) out.hint = parts[2].trim();
        return out;
      });
      const { data } = await http.post("/admin/bulk-import", { items });
      setResult(data);
      toast.success(`Imported ${data.ok}/${data.count} sightings into the moderation queue`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk import failed");
    } finally { setBusy(false); }
  };

  const uploadZip = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".zip")) return toast.error("Please pick a .zip file.");
    if (file.size > 100 * 1024 * 1024) return toast.error("Zip must be under 100 MB.");
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post("/admin/bulk-import/zip", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 240000,
      });
      setResult(data);
      toast.success(`Zip processed: ${data.ok}/${data.count} imported → moderation queue`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Zip upload failed");
    } finally { setBusy(false); if (zipRef.current) zipRef.current.value = ""; }
  };

  return (
    <div className="space-y-6">
      <div className="border border-black/15 p-6">
        <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-brand)] mb-2">
          Batch · up to 25 photos
        </div>
        <h3 className="font-serif-display text-2xl mb-3">Paste photo URLs — one per line</h3>
        <p className="text-sm text-[color:var(--np-text-2)] mb-3">
          Optional GPS &amp; hint per line: <code className="font-mono-tech text-xs bg-black/5 px-1">url|lat,lon|hint</code>.
          Each photo is uploaded, reverse-geocoded, identified by Gemini, and queued for your review.
        </p>
        <textarea
          data-testid="bulk-urls"
          value={urls}
          onChange={(e) => setUrls(e.target.value)}
          rows={8}
          placeholder="https://example.com/photo1.jpg|45.51,-122.68|near a pond
https://example.com/photo2.jpg
https://example.com/photo3.jpg|20.5937,78.9629"
          className="w-full bg-transparent border border-black/20 focus:border-[color:var(--np-brand)] outline-none p-3 font-mono-tech text-xs resize-none"
        />
        <button
          onClick={run}
          disabled={busy}
          data-testid="bulk-run"
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-[color:var(--np-text)] text-[color:var(--np-bg)] px-6 py-3 font-mono-tech text-[11px] tracking-[0.3em] uppercase hover:opacity-90 disabled:opacity-50"
        >
          <Sparkle size={14} weight="fill" />
          {busy ? "Importing…" : "Auto-identify & queue"}
        </button>
      </div>

      <div className="border border-black/15 p-6">
        <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-brand)] mb-2">
          Or drop a zip · EXIF-aware
        </div>
        <h3 className="font-serif-display text-2xl mb-3">Bulk import from a zip archive</h3>
        <p className="text-sm text-[color:var(--np-text-2)] mb-3">
          Great for festivals or expedition photo dumps. We read EXIF GPS from each JPG/PNG/WEBP,
          reverse-geocode it, run Gemini identify, and file everything into the moderation queue. Max
          25 photos + 100 MB per zip.
        </p>
        <input
          ref={zipRef}
          type="file"
          accept=".zip,application/zip"
          onChange={uploadZip}
          data-testid="bulk-zip-input"
          disabled={busy}
          className="block w-full text-sm text-[color:var(--np-text-2)] file:mr-4 file:py-2 file:px-4 file:border file:border-[color:var(--np-brand)] file:bg-transparent file:text-[color:var(--np-brand)] hover:file:bg-[color:var(--np-brand)] hover:file:text-white file:font-mono-tech file:text-[10px] file:tracking-[0.3em] file:uppercase file:cursor-pointer file:transition-colors"
        />
      </div>

      {result && (
        <div className="border border-black/15 p-6">
          <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-brand)] mb-2">
            Job {result.job_id} · {result.ok} ok / {result.failed} failed
          </div>
          <div className="space-y-2">
            {result.results.map((r) => (
              <div key={r.i} data-testid={`bulk-row-${r.i}`} className="grid grid-cols-12 gap-3 items-center py-2 border-b border-black/10 last:border-0 text-sm">
                <div className="col-span-1 font-mono-tech text-[10px] tracking-widest text-[color:var(--np-muted)]">#{r.i + 1}</div>
                <div className="col-span-1">
                  {r.status === "ok" ? <CheckCircle weight="fill" className="text-emerald-500" /> : <XCircle weight="fill" className="text-red-500" />}
                </div>
                <div className="col-span-5 font-serif-display text-base leading-tight">
                  {r.identified_as || "—"}
                  {r.confidence != null && <span className="ml-2 font-mono-tech text-[10px] tracking-widest text-[color:var(--np-muted)]">{Math.round(r.confidence * 100)}%</span>}
                </div>
                <div className="col-span-3 font-mono-tech text-[10px] tracking-widest text-[color:var(--np-muted)] uppercase truncate">
                  {r.region || "no gps"}
                </div>
                <div className="col-span-2 font-mono-tech text-[10px] text-[color:var(--np-muted)] truncate">
                  {r.obs_id || r.error || ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
